(() => {
  const STORAGE_KEY = "hm_wishlist_v2";
  const EVENT_NAME = "wishlist:updated";

  function normalise(list) {
    // Migrate old format ["handle", ...]
    if (Array.isArray(list) && list.length && typeof list[0] === "string") {
      return list.map((handle) => ({ handle, variantId: null }));
    }

    if (!Array.isArray(list)) return [];

    return list
      .filter((item) => item && typeof item.handle === "string")
      .map((item) => ({
        handle: item.handle,
        variantId: item.variantId ? Number(item.variantId) : null
      }));
  }

  function entryKey(handle, variantId) {
    return `${handle}::${variantId || ""}`;
  }

  function readFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return normalise(raw ? JSON.parse(raw) : []);
    } catch {
      return [];
    }
  }

  let wishlistList = readFromStorage();
  let wishlistKeys = new Set(wishlistList.map((item) => entryKey(item.handle, item.variantId)));

  function refreshCache() {
    wishlistList = readFromStorage();
    wishlistKeys = new Set(wishlistList.map((item) => entryKey(item.handle, item.variantId)));
    return wishlistList;
  }

  function write(list) {
    wishlistList = normalise(list);
    wishlistKeys = new Set(wishlistList.map((item) => entryKey(item.handle, item.variantId)));

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlistList));
    } catch {
      // If localStorage is blocked/full, keep the in-memory state for this page view.
    }

    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { list: wishlistList } }));
  }

  function read() {
    return wishlistList.slice();
  }

  function has(handle, variantId) {
    if (!handle) return false;
    return wishlistKeys.has(entryKey(handle, variantId));
  }

  function add(handle, variantId) {
    if (!handle) return;

    const safeVariantId = variantId ? Number(variantId) : null;
    const key = entryKey(handle, safeVariantId);

    if (!wishlistKeys.has(key)) {
      write([...wishlistList, { handle, variantId: safeVariantId }]);
    }
  }

  function remove(handle, variantId) {
    if (!handle) return;

    const key = entryKey(handle, variantId);
    write(wishlistList.filter((item) => entryKey(item.handle, item.variantId) !== key));
  }

  function toggle(handle, variantId) {
    if (!handle) return false;

    if (has(handle, variantId)) {
      remove(handle, variantId);
      return false;
    }

    add(handle, variantId);
    return true;
  }

  function count() {
    return wishlistList.length;
  }

  function moneyFormat(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const fmt = window.__wishlistMoneyFormat || window.Shopify.money_format || "${{amount}}";
      return window.Shopify.formatMoney(cents, fmt);
    }

    const value = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: window.Shopify?.currency?.active || "GBP"
      }).format(value);
    } catch {
      return `£${value.toFixed(2)}`;
    }
  }

  function escapeHtml(value) {
    return String(value || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function showWishlistToast({
    title,
    message,
    actionHref = "/pages/wishlist",
    actionText = "View wishlist"
  }) {
    let toast = document.querySelector("[data-wishlist-toast]");

    if (!toast) {
      toast = document.createElement("div");
      toast.className = "wishlist-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.setAttribute("data-wishlist-toast", "");
      document.body.appendChild(toast);
    }

    const isMobile = window.matchMedia("(max-width: 749px)").matches;
    const isNarrowMobile = window.matchMedia("(max-width: 359px)").matches;

    // Mobile uses a compact bottom-left snackbar so the assistance control can
    // remain bottom-right. Very narrow screens fall back above the control.
    if (isMobile) {
      Object.assign(toast.style, {
        left: isNarrowMobile ? "12px" : "14px",
        right: isNarrowMobile ? "12px" : "auto",
        bottom: isNarrowMobile
          ? "calc(82px + env(safe-area-inset-bottom))"
          : "calc(14px + env(safe-area-inset-bottom))",
        width: isNarrowMobile ? "auto" : "min(240px, calc(100vw - 150px))",
        padding: "12px 12px 11px",
        gap: "6px",
        borderRadius: "2px"
      });
    } else {
      Object.assign(toast.style, {
        left: "auto",
        right: "16px",
        bottom: "16px",
        width: "min(360px, calc(100vw - 32px))",
        padding: "14px 14px 12px",
        gap: "10px",
        borderRadius: "0"
      });
    }

    if (toast.__hideTimer) window.clearTimeout(toast.__hideTimer);

    toast.innerHTML = `
      <div>
        <p class="wishlist-toast__title">${escapeHtml(title)}</p>
        <p class="wishlist-toast__text">${escapeHtml(message)}</p>
        <div class="wishlist-toast__actions">
          <a class="wishlist-toast__link button button--primary" href="${escapeHtml(actionHref)}">${escapeHtml(actionText)}</a>
        </div>
      </div>
      <button type="button" class="wishlist-toast__close" aria-label="Close">×</button>
    `;

    if (isMobile) {
      const text = toast.querySelector(".wishlist-toast__text");
      const actions = toast.querySelector(".wishlist-toast__actions");
      const link = toast.querySelector(".wishlist-toast__link");
      const closeButton = toast.querySelector(".wishlist-toast__close");

      if (text) {
        Object.assign(text.style, {
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: "3",
          overflow: "hidden",
          fontSize: "13px",
          lineHeight: "1.35",
          marginTop: "4px"
        });
      }

      if (actions) {
        Object.assign(actions.style, {
          marginTop: "8px",
          gap: "0"
        });
      }

      if (link) {
        Object.assign(link.style, {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "auto",
          minWidth: "0",
          minHeight: "34px",
          height: "34px",
          padding: "0 12px",
          fontSize: "12px",
          fontWeight: "700",
          lineHeight: "1",
          letterSpacing: ".02em",
          textDecoration: "none"
        });
      }

      if (closeButton) {
        Object.assign(closeButton.style, {
          right: "-7px",
          top: "-7px",
          fontSize: "20px"
        });
      }
    }

    const closeButton = toast.querySelector(".wishlist-toast__close");
    const hide = () => toast.classList.remove("is-visible");

    closeButton?.addEventListener(
      "click",
      () => {
        hide();
        if (toast.__hideTimer) window.clearTimeout(toast.__hideTimer);
      },
      { once: true }
    );

    requestAnimationFrame(() => toast.classList.add("is-visible"));
    toast.__hideTimer = window.setTimeout(hide, 3500);
  }

  function setButtonsState(root = document) {
    root.querySelectorAll("[data-wishlist-toggle]").forEach((button) => {
      const handle = button.getAttribute("data-product-handle");
      const variantId = Number(button.getAttribute("data-variant-id") || 0) || null;
      const active = !!handle && has(handle, variantId);

      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function setHeaderCount(root = document) {
    const total = count();
    const displayTotal = total < 100 ? String(total) : "99+";

    root.querySelectorAll("[data-wishlist-count]").forEach((countEl) => {
      countEl.textContent = displayTotal;
    });

    root.querySelectorAll("[data-wishlist-count-bubble]").forEach((bubble) => {
      bubble.hidden = total === 0;
    });

    root.querySelectorAll("[data-wishlist-count-text]").forEach((textEl) => {
      textEl.textContent =
        total === 1 ? "1 item in wishlist" : `${total} items in wishlist`;
    });
  }

  function getVariantTitleFromMap(button, variantId) {
    if (!button || !variantId) return "";

    const raw = button.getAttribute("data-variant-title-map");
    if (!raw) return "";

    try {
      const map = JSON.parse(raw);
      return map[String(variantId)] || "";
    } catch {
      return "";
    }
  }

  function variantLabelForButton(button) {
    const title = button.getAttribute("data-variant-title") || "";

    if (!title || title === "Default Title") return "";
    return ` (${title})`;
  }

  function syncPdpWishlistVariant() {
    const button = document.querySelector('main [data-wishlist-toggle][data-product-handle]');
    if (!button) return;

    const form =
        button.closest('form[action^="/cart/add"]') ||
        document.querySelector('main form[action^="/cart/add"]');

    const variantInput = form?.querySelector('input[name="id"]');
    if (!variantInput) return;

    let lastVariantId = null;

    const update = () => {
        const variantId = Number(variantInput.value || 0) || null;

        if (variantId === lastVariantId) return;
        lastVariantId = variantId;

        button.setAttribute("data-variant-id", variantId ? String(variantId) : "");

        const title = getVariantTitleFromMap(button, variantId);
        if (title) {
        button.setAttribute("data-variant-title", title);
        } else {
        button.removeAttribute("data-variant-title");
        }

        setButtonsState(document);
    };

    update();

    variantInput.addEventListener("change", update);

    if (form) {
        form.addEventListener("change", () => {
        window.setTimeout(update, 0);
        });
    }

    document.addEventListener("change", (event) => {
        if (
        event.target.closest("variant-selects") ||
        event.target.closest("variant-radios") ||
        event.target.closest("[data-product-form]")
        ) {
        window.setTimeout(update, 0);
        }
    });
  }

  function initToggleHandler(root = document) {
    root.addEventListener("click", (event) => {
      const button = event.target.closest("[data-wishlist-toggle]");
      if (!button) return;

      event.preventDefault();

      const handle = button.getAttribute("data-product-handle");
      const variantId = Number(button.getAttribute("data-variant-id") || 0) || null;
      if (!handle) return;

      const wasActive = has(handle, variantId);
      toggle(handle, variantId);

      setButtonsState(document);
      setHeaderCount(document);

      const productTitle = button.getAttribute("data-product-title") || "Item";
      const variantLabel = variantLabelForButton(button);

      showWishlistToast({
        title: wasActive ? "Removed from wishlist" : "Added to wishlist",
        message: `${productTitle}${variantLabel}`,
        actionHref: "/pages/wishlist",
        actionText: "View wishlist"
      });
    });
  }

  function refreshUi(root = document) {
    setButtonsState(root);
    setHeaderCount(root);
  }

  function init() {
    initToggleHandler(document);
    syncPdpWishlistVariant();
    refreshUi(document);

    window.addEventListener(EVENT_NAME, () => {
      refreshUi(document);
    });

    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      refreshCache();
      refreshUi(document);
    });
  }

  window.HMWishlist = {
    STORAGE_KEY,
    EVENT_NAME,
    read,
    write,
    refreshCache,
    has,
    add,
    remove,
    toggle,
    count,
    moneyFormat,
    setButtonsState,
    setHeaderCount,
    refreshUi
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
